/**
 * Pure `hostMetrics` ingest shared by the web HostMetricsService and the
 * mobile wallboard host feed (WI 34539). Lifted unchanged from the web
 * service so both platforms key, order and carry samples the same way.
 */
import { carryDaemons, normalizeHostDaemons } from './host-daemons';
import type { HistoryPoint, HostMetric } from './host-metric.models';

/** Rolling buffer cap per host. Agents sample every 5 s, so ~4.2 h. */
export const HISTORY_MAX_POINTS = 3000;
/** No sample inside this window reads as offline in the hosts list. */
export const OFFLINE_AFTER_MS = 30_000;

/**
 * Normalize a machine id for comparison: trim, strip a trailing `.local`,
 * lowercase. Mirrors the node-side guard (OrdinalIgnoreCase + `.local` strip)
 * so the Roost keys and displays machines consistently with the executor.
 */
export function normalizeMachineId(host: string | null | undefined): string {
  if (!host) return '';
  let h = host.trim();
  if (h.toLowerCase().endsWith('.local')) {
    h = h.slice(0, -'.local'.length);
  }
  return h.toLowerCase();
}

export interface HostMetricsState {
  hosts: Map<string, HostMetric>;
  history: Map<string, HistoryPoint[]>;
}

/**
 * Apply one raw sample. Returns new maps (inputs are not mutated), or null
 * when the sample has no host id. A late sample never replaces a newer one,
 * but the daemons block is chosen on checkedAt, not on the heartbeat: the
 * CC's once-a-minute report routinely lands behind the agent's sample and
 * must still replace an older report (Bug 34528).
 */
export function ingestHostSample(
  state: HostMetricsState,
  raw: unknown,
  maxPoints = HISTORY_MAX_POINTS
): HostMetricsState | null {
  const parsed = normalizeHostMetric(raw);
  // One machine can have several reporters that disagree on case and on a
  // trailing .local, so key on the same machine id the executor matches on.
  const hostId = normalizeMachineId(parsed.hostId);
  if (!hostId) return null;
  const metric = { ...parsed, hostId };

  let hosts = state.hosts;
  const current = hosts.get(hostId);
  const late = !!current && isOlder(metric, current);
  const daemons = carryDaemons(current?.daemons, metric.daemons ?? null, late);
  if (!current || !late) {
    metric.daemons = daemons;
    hosts = new Map(hosts);
    hosts.set(hostId, metric);
  } else if (daemons !== (current.daemons ?? null)) {
    hosts = new Map(hosts);
    hosts.set(hostId, { ...current, daemons });
  }

  const history = new Map(state.history);
  const buf = history.get(hostId)?.slice() ?? [];
  // Insert in timestamp order: host detail draws the buffer in array order
  // and reads the newest point from the tail.
  const ts = new Date(metric.lastHeartbeat).getTime();
  let at = buf.length;
  while (at > 0 && new Date(buf[at - 1].ts).getTime() > ts) at--;
  buf.splice(at, 0, {
    ts: metric.lastHeartbeat,
    cpu: metric.cpu.percent,
    mem: metric.mem.percent,
    disk: metric.disk.percent,
    gpu: metric.gpu?.utilPercent ?? 0,
    netRx: metric.network?.rxBytesPerSec ?? 0,
    netTx: metric.network?.txBytesPerSec ?? 0,
  });
  if (buf.length > maxPoints) buf.splice(0, buf.length - maxPoints);
  history.set(hostId, buf);
  return { hosts, history };
}

/** Hosts sorted by name with `online` re-derived from heartbeat age. */
export function hostList(
  hosts: Map<string, HostMetric>,
  nowMs: number
): HostMetric[] {
  return Array.from(hosts.values())
    .map((h) => ({
      ...h,
      online:
        h.online &&
        nowMs - new Date(h.lastHeartbeat).getTime() < OFFLINE_AFTER_MS,
    }))
    .sort((a, b) => a.hostname.localeCompare(b.hostname));
}

function isOlder(a: HostMetric, b: HostMetric): boolean {
  return (
    new Date(a.lastHeartbeat).getTime() < new Date(b.lastHeartbeat).getTime()
  );
}

/**
 * Azure Functions SignalR may serialize C# records as PascalCase on the wire;
 * normalize to camelCase so consumers see one stable shape.
 */
export function normalizeHostMetric(raw: unknown): HostMetric {
  const r = (raw ?? {}) as Record<string, unknown>;
  const pickFrom = <T>(
    obj: Record<string, unknown>,
    camelKey: string,
    pascalKey: string,
    fallback: T
  ): T => (obj[camelKey] ?? obj[pascalKey] ?? fallback) as T;
  const pick = <T>(camelKey: string, pascalKey: string, fallback: T): T =>
    pickFrom(r, camelKey, pascalKey, fallback);

  const cpu = pick<Record<string, unknown>>('cpu', 'Cpu', {});
  const mem = pick<Record<string, unknown>>('mem', 'Mem', {});
  const disk = pick<Record<string, unknown>>('disk', 'Disk', {});
  const gpu = pick<Record<string, unknown> | null>('gpu', 'Gpu', null);
  const net = pick<Record<string, unknown> | null>('network', 'Network', null);
  const num = (o: Record<string, unknown>, c: string, p: string) =>
    Number(pickFrom<number>(o, c, p, 0));

  return {
    hostId: pick<string>('hostId', 'HostId', ''),
    hostname: pick<string>('hostname', 'Hostname', ''),
    online: pick<boolean>('online', 'Online', true),
    lastHeartbeat: pick<string>(
      'lastHeartbeat',
      'LastHeartbeat',
      new Date().toISOString()
    ),
    cpu: {
      percent: num(cpu, 'percent', 'Percent'),
      cores: num(cpu, 'cores', 'Cores'),
      model: pickFrom<string | null>(cpu, 'model', 'Model', null),
    },
    mem: {
      usedBytes: num(mem, 'usedBytes', 'UsedBytes'),
      totalBytes: num(mem, 'totalBytes', 'TotalBytes'),
      percent: num(mem, 'percent', 'Percent'),
    },
    disk: {
      usedBytes: num(disk, 'usedBytes', 'UsedBytes'),
      totalBytes: num(disk, 'totalBytes', 'TotalBytes'),
      percent: num(disk, 'percent', 'Percent'),
      mount: pickFrom<string | undefined>(disk, 'mount', 'Mount', undefined),
    },
    gpu: gpu
      ? {
          name: pickFrom<string>(gpu, 'name', 'Name', ''),
          utilPercent: num(gpu, 'utilPercent', 'UtilPercent'),
          memUsedMiB: num(gpu, 'memUsedMiB', 'MemUsedMiB'),
          memTotalMiB: num(gpu, 'memTotalMiB', 'MemTotalMiB'),
        }
      : null,
    network: net
      ? {
          rxBytesPerSec: num(net, 'rxBytesPerSec', 'RxBytesPerSec'),
          txBytesPerSec: num(net, 'txBytesPerSec', 'TxBytesPerSec'),
        }
      : null,
    os: pick<string | null>('os', 'Os', null),
    kernel: pick<string | null>('kernel', 'Kernel', null),
    arch: pick<string | null>('arch', 'Arch', null),
    rynCoreRoot: pick<string | null>('rynCoreRoot', 'RynCoreRoot', null),
    daemons: normalizeHostDaemons(pick<unknown>('daemons', 'Daemons', null)),
  };
}
